import {
  computed,
  effect,
  signal,
  Signal,
  useSignalEffect,
} from '@preact/signals-react';
import produce from 'immer';
import { useContext, useEffect, useRef } from 'react';
import { createSignalsContext } from '@/signals/signal-context';
import { AiComponentTab } from '@/features/editor/widgets/ai-component/models';
import {
  EditorChangeKind,
  EditorDeclarativeBlock,
} from '@/webapi/use-experience-api';
import { SessionListEntry } from '@/features/ai-agents/shared/use-ai-metadata-api';
import { CompoundChange } from '@/pkg/sdk';
import { AiChatMessage } from '@/features/ai-agents/shared/models';
import { extractCodeBlocks } from '@/features/editor/widgets/code-editors/use-code-editor';
import { hash } from '@/utils/cache';
import { EditorContext } from '@/features/editor/context/editor-context';
import {
  AiComponentHtmlAttribute,
  AiComponentJsVariable,
  AiComponentVisualManifest,
  AiManifestComponent,
  VideoAttributes,
} from '@/pkg/ai';
import {
  ParsedCssRule,
  useCssManipulator,
} from '@/features/editor/widgets/ai-component/manipulators/use-css-manipulator';
import {
  EDITOR_ACTIVE_BLOCK_CACHE,
  hashObject,
  useSignalCachedAutoSave,
} from '@/components/hooks/use-cached-auto-save';
import { useJavascriptManipulator } from '@/features/editor/widgets/ai-component/manipulators/use-js-manipulator';
import { useHtmlManipulator } from '@/features/editor/widgets/ai-component/manipulators/use-html-manipulator';
import { useCompoundChangeRenderer } from '@/features/editor/widgets/ai-component/use-compound-change-renderer';
import { useVisualManifestBuilder } from '@/features/editor/widgets/ai-component/manipulators/use-visual-manifest-builder';
import {
  AiVisualEditMessage,
  NewElementMessage,
} from '@/features/editor/context/use-device-preview';

export type VisualGroupInfo = {
  group: string;
  selector: string;
};

export type AiComponentContextProps = {
  originalChange: EditorDeclarativeBlock;
};

export interface AiComponentState {
  newChange: Signal<EditorDeclarativeBlock>;
  lastSavedChange: Signal<EditorDeclarativeBlock>;
  selectedTab: Signal<AiComponentTab>;
  highlightedVisualGroup?: Signal<VisualGroupInfo | undefined>;
  selectedCodeTab: Signal<`HTML` | `CSS` | `JS`>;
  sessionId: Signal<string>;

  isProcessing: Signal<boolean>;
  isProcessingVisualManifest: Signal<boolean>;

  isFullScreen: Signal<boolean>;
  isComponentReady: Signal<boolean>;
  hasChanges: Signal<boolean>;
  hasVisualEditManifest: Signal<boolean>;
  sessionData: Signal<Record<string, any>>;
}

export interface AiComponentActions {
  selectTab: (tab: AiComponentTab) => void;
  onSelectedCodeTab: (tab: `HTML` | `CSS` | `JS`) => void;
  onNewSessionId: (session: SessionListEntry) => void;
  onNewMessageSent: (message: AiChatMessage) => void;
  onPromptReply: (
    agentMessage: AiChatMessage,
    userMessage: AiChatMessage,
    sessionId: string,
  ) => void;
  setHtml: (html: string) => void;
  setCss: (css: string) => void;
  setJs: (js: string) => void;
  setEditManifest: (manifest: string) => void;
  toggleFullScreen: () => void;
  applyChanges: () => void;
  discardChanges: () => void;
  saveLocalChanges: () => void;
  discardLocalChanges: () => void;

  getTextContent: (selector: string) => string | undefined;
  setTextContent: (selector: string, value: string, richText?: boolean) => void;
  getHtmlContent: (selector: string) => string | undefined;
  setHtmlContent: (selector: string, value: string) => void;
  getAttribute: (
    selector: string,
    attribute: AiComponentHtmlAttribute,
  ) => string;
  setAttribute: (
    selector: string,
    attribute: AiComponentHtmlAttribute,
    value: string,
  ) => void;
  getVideoAttributes: (selector: string) => VideoAttributes | undefined;
  setVideoAttributes: (selector: string, attrs: VideoAttributes) => void;

  onLoadingChange: (loading: boolean) => void;

  getCssRule: (selector: string) => ParsedCssRule | undefined;
  setCssRule: (input: ParsedCssRule) => void;
  getManifestVariables: () => AiComponentJsVariable[];
  setVariable: (variable: AiComponentJsVariable) => void;
  isFirstInGroup: (component: AiManifestComponent) => boolean;
}

function createState(props?: AiComponentContextProps): AiComponentState {
  const newChange = signal(
    produce(props.originalChange, (draft) => {
      draft.editorKind = EditorChangeKind.NEW_COMPONENT;
      (draft.block.value as CompoundChange).isAiComponent = true;
    }),
  );
  const lastSavedChange = signal(props.originalChange);

  const hasVisualEditManifest = computed(() => {
    const block = newChange.value.block.value as CompoundChange;
    return !!block.editManifest;
  });

  const selectedTab = signal(
    hasVisualEditManifest?.value ? AiComponentTab.EDIT : AiComponentTab.PROMPT,
  );
  const selectedCodeTab = signal<`HTML` | `CSS` | `JS`>(`HTML`);
  const sessionId = computed(
    () => (newChange.value.block.value as CompoundChange).aiSessionId,
  );

  const isComponentReady = computed(() => {
    if (isProcessing.value) return false;
    const block = newChange.value.block.value as CompoundChange;
    const isPlaceholder =
      block?.html?.includes(`__loomi_element_placeholder`) ||
      block?.html?.includes(`__visually_element_placeholder`);
    return !isPlaceholder && block.html?.length > 5 && !!block.aiSessionId;
  });

  const isFullScreen = signal(false);

  const initialHash = hashChange(props?.originalChange);

  const hasChanges = computed(
    () => initialHash !== hashChange(newChange.value),
  );

  const isProcessing = signal(false);
  const isProcessingVisualManifest = signal(false);

  const sessionData = signal<Record<string, any>>({});

  const highlightedVisualGroup = signal<VisualGroupInfo | undefined>(undefined);

  return {
    isFullScreen,
    selectedTab,
    isProcessing,
    newChange,
    selectedCodeTab,
    sessionId,
    isComponentReady,
    hasChanges,
    hasVisualEditManifest,
    isProcessingVisualManifest,
    sessionData,
    lastSavedChange,
    highlightedVisualGroup,
  };
}

function createActions(state: AiComponentState): AiComponentActions {
  const {
    inspectorNav: { gotoChangelog, gotoAppCatalog },
    experienceState: { upsertEditorChange, currentExperience },
    devicePreview: {
      editComponent,
      editorState: { device, installedFonts, mostPopularFont },
    },
    deviceNavigation: { previewUrl, urlToStorePage },
  } = useContext(EditorContext);

  const mountsCount = useRef(0);

  useSignalCachedAutoSave(
    state.newChange,
    currentExperience.id,
    EDITOR_ACTIVE_BLOCK_CACHE,
  );

  const setHtml = (html: string) => {
    state.newChange.value = produce(state.newChange.value, (draft) => {
      (draft.block.value as CompoundChange).html = html;
    });
  };

  const setCss = (css: string) => {
    state.newChange.value = produce(state.newChange.value, (draft) => {
      (draft.block.value as CompoundChange).css = css;
    });
  };

  const setJs = (js: string) => {
    state.newChange.value = produce(state.newChange.value, (draft) => {
      (draft.block.value as CompoundChange).js = js;
    });
  };

  const { rerender } = useCompoundChangeRenderer(
    state.newChange,
    state.isProcessing,
  );

  const selectTab = (tab: AiComponentTab) => {
    if (state?.selectedTab.peek() === tab) {
      state.selectedTab.value = AiComponentTab.PROMPT;
    } else {
      state.selectedTab.value = tab;
    }
  };

  const onSelectedCodeTab = (tab: `HTML` | `CSS` | `JS`) => {
    state.selectedCodeTab.value = tab;
  };

  const onNewSessionId = (session: SessionListEntry) => {
    state.newChange.value = produce(state.newChange.value, (draft) => {
      (draft.block.value as CompoundChange).aiSessionId = session.session_id;
    });
  };

  const onPromptReply = (
    agentMessage: AiChatMessage,
    _: AiChatMessage,
    sessionId: string,
  ) => {
    state.newChange.value = produce(state.newChange.value, (draft) => {
      const block = draft.block.value as CompoundChange;
      block.aiSessionId = sessionId;
      let {
        html0: html,
        css0: css,
        javascript0: js,
      } = extractCodeBlocks(agentMessage.content);
      let aiSectionId = ``;
      if (html) {
        const div = document.createElement(`div`);
        div.innerHTML = html;
        const aiSection = div.querySelector(`div[id*="loomi_addon"]`);
        aiSectionId = aiSection?.id;
        const name = aiSection.getAttribute(`name`);
        if (name) {
          draft.hint = name;
        }
        if (aiSectionId) {
          html = html.replaceAll(aiSectionId, draft.editorId);
        }
        block.html = html;
      }
      if (css) {
        if (aiSectionId) {
          css = css.replaceAll(`#${aiSectionId}`, `#${draft.editorId}`);
        }
        block.css = css;
      }
      if (js) {
        if (aiSectionId) {
          js = js.replaceAll(aiSectionId, draft.editorId);
        }
        block.js = js;
      }
    });

    rerender();
  };

  const setEditManifest = (manifest: string) => {
    try {
      const parsedManifest = JSON.parse(manifest) as AiComponentVisualManifest;
      state.newChange.value = produce(state.newChange.value, (draft) => {
        (draft.block.value as CompoundChange).editManifest = parsedManifest;
      });
    } catch (error) {
      console.error(`Failed to parse edit manifest:`, error);
    }
  };

  const toggleFullScreen = () => {
    state.isFullScreen.value = !state.isFullScreen.value;
  };

  const applyChanges = () => {
    upsertEditorChange(state.newChange.value);
    gotoChangelog();
  };

  const discardChanges = () => {
    gotoChangelog();
  };

  const saveLocalChanges = () => {
    state.lastSavedChange.value = state.newChange.value;
    selectTab(AiComponentTab.PROMPT);
  };

  const discardLocalChanges = () => {
    state.newChange.value = state.lastSavedChange.value;
    selectTab(AiComponentTab.PROMPT);
  };

  effect(() => {
    const pageType = urlToStorePage(previewUrl);

    state.sessionData.value = {
      device,
      most_popular_font: mostPopularFont,
      installed_fonts: installedFonts,
      page_url: previewUrl,
      page_type: pageType.kind,
      page_title: pageType.caption,
      section_id: state.newChange.value.editorId,
    };
  });

  effect(() => {
    if (
      state.isFullScreen.value &&
      state.selectedTab.value !== AiComponentTab.CODE
    ) {
      state.isFullScreen.value = false;
    }
  });

  effect(() => {
    if (mountsCount.current === 0) {
      editComponent(state.newChange.value.editorSelector);
      mountsCount.current += 1;
    }
  });

  const onLoadingChange = (loading: boolean) => {
    state.isProcessing.value = loading;
  };

  const { setCssRule, getCssRule, parsedCss } = useCssManipulator(
    state.newChange,
    setCss,
  );

  const { setVariable, getManifestVariables, jsVariables, parsedJs } =
    useJavascriptManipulator(state.newChange, setJs);

  const {
    getTextContent,
    setTextContent,
    getHtmlContent,
    setHtmlContent,
    getAttribute,
    setAttribute,
    getVideoAttributes,
    setVideoAttributes,
    removeByGroupName,
    removeAllByGroupName,
    parsedHtml,
  } = useHtmlManipulator(state.newChange, setHtml);

  const { isFirstInGroup } = useVisualManifestBuilder(
    state.newChange,
    parsedHtml,
    parsedCss,
    parsedJs,
    jsVariables,
  );

  const visualEditHandler = (ev: any) => {
    const detail = ev?.detail as AiVisualEditMessage;
    if (detail.subAction === `scroll`) {
      state.highlightedVisualGroup.value = {
        group: detail.group,
        selector: detail.selector,
      };
      if (state.selectedTab.value !== AiComponentTab.EDIT) {
        selectTab(AiComponentTab.EDIT);
      }
      return;
    }
    if (detail.subAction === `remove`) {
      removeByGroupName(detail.group, detail.selector);
    }
    if (detail.subAction === `removeAll`) {
      removeAllByGroupName(detail.group, detail.selector);
    }
    if ([`addAfter`, `addBefore`].includes(detail.subAction)) {
      upsertEditorChange(state.newChange.value);
      gotoAppCatalog({
        action: `create`,
        kind: detail.subAction.replace(`add`, `append`),
        selector: detail.selector,
      } as NewElementMessage);
    }
  };

  useSignalEffect(() => {
    window[`vslyAiEditorSelectedTab`] = state.selectedTab.value.toLowerCase();
  });

  useEffect(() => {
    window.addEventListener(`ai-visual-edit`, visualEditHandler);
    return () => {
      window.removeEventListener(`ai-visual-edit`, visualEditHandler);
      window[`vslyAiEditorSelectedTab`] = undefined;
    };
  }, []);

  const onNewMessageSent = (message: AiChatMessage) => {
    const block = state.newChange.value?.block?.value as CompoundChange;
    const html = block.html || ``;
    const css = block.css || ``;
    const js = block.js || ``;

    const isPlaceholder =
      html?.includes(`__loomi_element_placeholder`) ||
      html?.includes(`__visually_element_placeholder`);

    if (!isPlaceholder) {
      if (!message.session_data) {
        message.session_data = {};
      }
      message.session_data[`html_code`] = html;
      message.session_data[`css_code`] = css;
      message.session_data[`js_code`] = js;
    }
  };

  return {
    selectTab,
    onSelectedCodeTab,
    onNewSessionId,
    onNewMessageSent,
    onPromptReply,
    setHtml,
    setCss,
    setJs,
    toggleFullScreen,
    discardChanges,
    applyChanges,
    getCssRule,
    setCssRule,
    setEditManifest,
    setVariable,
    getManifestVariables,
    getTextContent,
    setTextContent,
    getHtmlContent,
    setHtmlContent,
    getAttribute,
    setAttribute,
    getVideoAttributes,
    setVideoAttributes,
    onLoadingChange,
    saveLocalChanges,
    discardLocalChanges,
    isFirstInGroup,
  };
}

export const [AiComponentContextProvider, useAiComponentContext] =
  createSignalsContext(createState, createActions);

function hashChange(originalChange: EditorDeclarativeBlock | undefined) {
  if (!originalChange) return ``;
  const block = originalChange.block.value as CompoundChange;
  return hash(
    `${block.html || ``}${block.css || ``}${block.js || ``}${
      block.aiSessionId || ``
    }${hashObject(block.editManifest || {})}`,
  );
}
